import { ORGANIZATION_ADMIN_NAME } from '@lib/constants/permissions'
import type { AssignableRolesDto, UserDto } from '@lib/dto/user.dto'
import type { TenantRoleReach } from '@lib/enums/tenant-role-reach.enum'
import type { InviteUserInput } from '@lib/input/user.input'
import { isOrganizationAdmin } from '@/auth/roles'
import type { User } from '@/schemas/auth.schema'

/**
 * Which role a person holds, or is being given, as the people screens read it
 * (docs/custom-roles.spec.md): organization admin, a client, or staff on one of
 * the company's roles. One string per role so a select, a filter and a
 * comparison all use the same value.
 */
export type PersonRoleKey = 'organizationAdmin' | 'client' | `role:${string}`

/** One of the company's roles, as the role picker lists it. */
export type CompanyRole = AssignableRolesDto['roles'][number]

/** The key for staff on this company role. */
export function staffRoleKey(roleId: string): PersonRoleKey {
  return `role:${roleId}`
}

/** A select's value as a role key, or null for one that isn't. */
export function toPersonRoleKey(value: string): PersonRoleKey | null {
  if (value === 'organizationAdmin' || value === 'client') return value
  return value.startsWith('role:') && value.length > 'role:'.length ? staffRoleKey(value.slice('role:'.length)) : null
}

/** The company role a key names, or null for organization admin, a client, or a role the company doesn't have. */
export function companyRoleOf(key: PersonRoleKey | null, roles: AssignableRolesDto | undefined): CompanyRole | null {
  if (key === null || !key.startsWith('role:')) return null
  return roles?.roles.find((role) => staffRoleKey(role.id) === key) ?? null
}

/** The role a person holds now. Staff the list didn't name a role for read as the built-in one, which is what they hold. */
export function personRoleKeyOf(person: Pick<UserDto, 'role' | 'staffRole'>, roles: AssignableRolesDto | undefined): PersonRoleKey | null {
  if (person.role === 'ORGANIZATION_ADMIN') return 'organizationAdmin'
  if (person.role === 'CLIENT') return 'client'
  if (person.role !== 'VENUE_ADMIN') return null
  if (person.staffRole !== null) return staffRoleKey(person.staffRole.id)
  const builtIn = roles?.roles.find((role) => role.builtIn)
  return builtIn === undefined ? null : staffRoleKey(builtIn.id)
}

/** How far the role a key names reaches: null for anything that isn't a company role. */
export function reachOf(key: PersonRoleKey | null, roles: AssignableRolesDto | undefined): TenantRoleReach | null {
  return companyRoleOf(key, roles)?.reach ?? null
}

/** What an invite or a change sends for this role. */
export function roleInputOf(key: PersonRoleKey): Pick<InviteUserInput, 'role' | 'tenantRoleId'> {
  if (key === 'organizationAdmin') return { role: 'ORGANIZATION_ADMIN' }
  if (key === 'client') return { role: 'CLIENT' }
  return { role: 'VENUE_ADMIN', tenantRoleId: key.slice('role:'.length) }
}

/** What to call a person's role in a list: their company role's name, or the kind of account. */
export function personRoleLabel(person: Pick<UserDto, 'role' | 'staffRole'>): string {
  if (person.role === 'ORGANIZATION_ADMIN') return ORGANIZATION_ADMIN_NAME
  if (person.role === 'CLIENT') return 'Client'
  return person.staffRole?.name ?? 'Staff'
}

/**
 * Whether the viewer may change this person, as the API's guardrail decides it
 * (api `users/people-guardrail.ts`): an organization admin anyone; staff with
 * People Edit only other staff, on a role they could give, who work where they
 * do (`worksWithViewer`). The API refuses the rest; this keeps the screen from
 * offering it.
 */
export function managesPerson(
  viewer: User | null | undefined,
  person: Pick<UserDto, 'id' | 'role' | 'staffRole'>,
  roles: AssignableRolesDto | undefined,
  worksWithViewer: boolean,
): boolean {
  if (viewer == null) return false
  if (isOrganizationAdmin(viewer)) return true
  if (person.id === viewer.id || person.role !== 'VENUE_ADMIN') return false
  const role = companyRoleOf(personRoleKeyOf(person, roles), roles)
  return role !== null && role.mayGive && worksWithViewer
}
